import { Api, LogService } from "../../../Services";
import { AppThunk } from "../../Store";
import { Actions } from "./AgriSolutionSlice";
import GeneralState from "../General";
import AgriSolutionState from ".";
import { TimeUtils } from "../../../Utils";
import { getSavedItemsRequest, getSavedObjRequest, getChangeRecipeRequest } from "./AgriSolutionUtils";
import { DEFAULT_TEMPERATURE } from "../../../Constants";
import { AddRawMaterialRequest } from "../../../Models/ServerModels/RawMaterials/AddRawMaterialsRequest";
import { BlendStat, ChangeRecipeRequest, SavedItemsRequest } from "../../../Models";
import { ZERO } from "../../../Utils/Consts";
import { SolutionState } from "../..";
import { shareableItemTypes } from "../../../Utils/Enums";
import { useSelector } from "react-redux";
import SavedItemsState from "../SavedItems";
import { getAllSavedItems } from "../SavedItems/SavedItemsThunks";

export const saveCropStage =
    (onSuccess: () => void): AppThunk =>
    async (dispatch: any, getState: any) => {
        try {
            await dispatch(Actions.setSaveInProgress(true));
            await dispatch(saveProcess());
            await dispatch(Actions.setSoilStateCanBeNavigated(true));
            await dispatch(updateCropStateTemperature());
            await dispatch(Actions.setSaveInProgress(false));
            onSuccess();
        } catch (ex) {
            LogService.logError(`Exception occurred in saveCropStage: ${ex}`);
        } finally {
            await dispatch(Actions.setSaveInProgress(false));
        }
    };
export const saveSoilState =
    (onSuccess: () => void): AppThunk =>
    async (dispatch: any, getState: any) => {
        try {
            await dispatch(Actions.setSaveInProgress(true));
            await dispatch(calculateSolutionProduct());

            await dispatch(Actions.setSaveInProgress(false));
            await dispatch(Actions.setResultsPageCanBeNavigated(true));
            onSuccess && onSuccess();
        } catch (ex) {
            LogService.logError(`Exception occurred in saveSoilState: ${ex}`);
        } finally {
            await dispatch(Actions.setSaveInProgress(false));
        }
    };
export const calculateSolutionProduct = (): AppThunk => async (dispatch: any, getState: any) => {
    try {
        const request: any = getSavedObjRequest(getState);
        const customizeSolutionResponse = await Api.Calculations.calculateCustomizedSolutionProduct(request);
        const readyMadeResponse = await Api.Calculations.calculateRecommendedSolutionProduct(request);

        if (
            customizeSolutionResponse &&
            customizeSolutionResponse.statusCode === 200 &&
            customizeSolutionResponse.data &&
            readyMadeResponse &&
            readyMadeResponse.statusCode === 200
        ) {
            if (readyMadeResponse.data) {
                await dispatch(AgriSolutionState.Actions.setRecommendedSolution(readyMadeResponse.data && readyMadeResponse.data.mostRecommendedFormula));
                await dispatch(AgriSolutionState.Actions.setShelfSolutionReleaseGraph(readyMadeResponse.data && readyMadeResponse.data.releaseGraphValues));
                await dispatch(AgriSolutionState.Actions.setShelfSolutionCurrentDayReleaseRemainValues(readyMadeResponse.data.currentDayReleaseRemainValues));
                dispatch(AgriSolutionState.Actions.setRecommendedSolutionMaterialCode(readyMadeResponse.data.materialCode));
            } else {
                await dispatch(AgriSolutionState.Actions.setRecommendedSolution(null));
                await dispatch(AgriSolutionState.Actions.setShelfSolutionReleaseGraph(null));
                await dispatch(AgriSolutionState.Actions.setShelfSolutionCurrentDayReleaseRemainValues([]));
                dispatch(AgriSolutionState.Actions.setRecommendedSolutionMaterialCode(null));
            }
            await dispatch(
                AgriSolutionState.Actions.setCustomizedSolutionCurrentDayReleaseRemainValues(customizeSolutionResponse.data.currentDayReleaseRemainValues)
            );
            await dispatch(getAllRawMaterials());
            await dispatch(
                Actions.setResultsState({
                    customizedSolution: customizeSolutionResponse.data,
                    recommendedSolution: readyMadeResponse.data,
                })
            );

            await dispatch(Actions.setBestSolution(customizeSolutionResponse.data.bestSuitFormula));
            await dispatch(Actions.setCustomizedFormulaByUser(customizeSolutionResponse.data.bestSuitFormula));
            await dispatch(Actions.removeItemFromAllCoatedMaterials({ coated: customizeSolutionResponse.data.recipe.coated }));
            await dispatch(Actions.removeItemFromAllUncoatedMaterials({ uncoated: customizeSolutionResponse.data.recipe.unCoated }));
            dispatch(AgriSolutionState.Actions.setReleaseGraph(customizeSolutionResponse.data.releaseGraphValues));
            dispatch(AgriSolutionState.Actions.setUpdatedBestSolution(null));
            dispatch(
                AgriSolutionState.Actions.setUpdatedCoatingPercentage({
                    n: null,
                    p: null,
                    k: null,
                })
            );

            await dispatch(Actions.setRecipe(customizeSolutionResponse.data.recipe));
            await dispatch(Actions.setBlendPrecentage(100));
            await dispatch(Actions.setCropDemand(customizeSolutionResponse.data.cropDemand));
            await dispatch(Actions.setBestSolutionCoatings(customizeSolutionResponse.data.bestSuitFormulaCoatings));

            await dispatch(Actions.setBestSolutionNutrients(customizeSolutionResponse.data.formulaNutrients));

            await dispatch(Actions.setBestSolution(customizeSolutionResponse.data.bestSuitFormula));
            await dispatch(Actions.setBestSolutionCoatings(customizeSolutionResponse.data.bestSuitFormulaCoatings));
            await dispatch(AgriSolutionState.Actions.setApplicationVolume(customizeSolutionResponse.data.applicationVolume));
        } else if (
            customizeSolutionResponse.statusCode === 401 ||
            (customizeSolutionResponse as any).statusCode === 401 ||
            readyMadeResponse.statusCode === 401 ||
            (readyMadeResponse as any).statusCode === 401
        ) {
            const userWantsToBeRemembered = useSelector(GeneralState.Selectors.selectUserWantsToBeRemembered);
            await dispatch(GeneralState.Actions.logOut(userWantsToBeRemembered));
        } else {
            throw "Bad response from server";
        }
    } catch (ex) {
        LogService.logError(`Exception occurred in calculateSolutionProduct: ${ex}`);
        throw ex;
    }
};
export const addRawMaterials =
    (request: AddRawMaterialRequest): AppThunk =>
    async (dispatch: any) => {
        try {
            const rawMaterialsData = await Api.RawMaterials.addRawMaterials(request);
            if (rawMaterialsData && rawMaterialsData.statusCode === 200) {
                await dispatch(getAllRawMaterials());
                await dispatch(AgriSolutionState.Thunks.getRawMaterialBank());
                const newItem: BlendStat = {
                    id: rawMaterialsData.data.id,
                    name: rawMaterialsData.data.name,
                    precentage: ZERO,
                };
                const isCoated = false;
                dispatch(AgriSolutionState.Actions.addItemToRecipe({ newItem, isCoated }));
            } else {
                LogService.logError(` add raw materials data request had failed. Response: ${rawMaterialsData}`);
                if (rawMaterialsData && rawMaterialsData.statusCode === 400) {
                    await dispatch(getAllRawMaterials());
                }
                // TODO: show error modal!
            }
        } catch (ex) {
            LogService.logError(`Exception occurred in addRawMaterials: ${ex}`);
            // TODO: show error modal!
        }
    };
export const share =
    (savedItemId: number, type: number): AppThunk =>
    async (dispatch: any) => {
        try {
            const shareData = await Api.Utils.share(savedItemId, type);
            if (shareData && shareData.statusCode === 200) {
                dispatch(AgriSolutionState.Actions.setShareLink(shareData.data.callback));
                dispatch(SolutionState.Actions.setShareLink(shareData.data.callback));
            } else {
                LogService.logError(` share request had failed. Response: ${shareData}`);
            }
        } catch (ex) {
            LogService.logError(`Exception occurred in shareData: ${ex}`);
        }
    };
export const changeRecipe = (): AppThunk => async (dispatch: any, getState: any) => {
    try {
        const request: ChangeRecipeRequest = getChangeRecipeRequest(getState);
        const changeRecipeResponse = await Api.Calculations.changeRecipe(request);

        if (changeRecipeResponse && changeRecipeResponse.statusCode === 200 && changeRecipeResponse.data) {
            dispatch(AgriSolutionState.Actions.setBestSolutionNutrients(changeRecipeResponse.data.formula));
            dispatch(AgriSolutionState.Actions.setReleaseGraphType(shareableItemTypes.agri_multicote_customize));
            dispatch(AgriSolutionState.Actions.setReleaseGraph(changeRecipeResponse.data.releaseGraphValues));
            dispatch(AgriSolutionState.Actions.setUpdatedBestSolution(changeRecipeResponse.data.solutionFormula));
            dispatch(AgriSolutionState.Actions.setUpdatedCoatingPercentage(changeRecipeResponse.data.coatingPrecentage));
        } else {
            LogService.logError(` change recipe data request had failed. Response: ${changeRecipeResponse}`);
            if (changeRecipeResponse && changeRecipeResponse.statusCode === 400) {
                changeRecipeResponse.error && dispatch(GeneralState.Actions.setChangeRecipeErrorFromServer(changeRecipeResponse.error));
            }
        }
    } catch (ex) {
        LogService.logError(`Exception occurred in changeRecipe: ${ex}`);
    }
};
export const getAllRawMaterials = (): AppThunk => async (dispatch: any) => {
    try {
        const rawMaterialsData = await Api.RawMaterials.getAllRawMaterials();
        if (rawMaterialsData && rawMaterialsData.statusCode === 200 && rawMaterialsData.data) {
            dispatch(Actions.setCoatedRawMaterials(rawMaterialsData.data.coated));
            dispatch(Actions.setUnCoatedRawMaterials(rawMaterialsData.data.unCoated));
        } else {
            LogService.logError(`get all raw materials data request had failed. Response: ${rawMaterialsData}`);
        }
    } catch (ex) {
        LogService.logError(`Exception occurred in getAllRawMaterials: ${ex}`);
    }
};
export const deleteRawMaterialById =
    (rawMaterialId: number): AppThunk =>
    async (dispatch: any, getState: any) => {
        try {
            const response = await Api.RawMaterials.deleteRawMaterial(rawMaterialId);
            if (response && response.statusCode === 200) await dispatch(getRawMaterialBank());
        } catch (ex) {
            LogService.logError(`Exception occurred in deleteRawMaterialById: ${ex}`);
        }
    };
export const getRawMaterialBank = (): AppThunk => async (dispatch: any) => {
    try {
        const rawMaterialsBank = await Api.RawMaterials.getRawMaterialBank();
        if (rawMaterialsBank && rawMaterialsBank.statusCode === 200 && rawMaterialsBank.data) dispatch(Actions.setRawMaterialBank(rawMaterialsBank.data));
    } catch (ex) {
        LogService.logError(`Exception occurred in getRawMaterialBank: ${ex}`);
    }
};
export const updateCropStateTemperature = (): AppThunk => async (dispatch: any, getState: any) => {
    try {
        const state = getState();
        const { activeMetricMethod } = GeneralState.Selectors.selectMetricMethods(state);
        const cropState = AgriSolutionState.Selectors.selectCropsState(state);
        const datesRange = TimeUtils.getDatesRange(cropState.plantingDate as string, cropState.finalHarvestDate as string);
        const defaultValue = (DEFAULT_TEMPERATURE as any)[activeMetricMethod as any];

        await dispatch(
            AgriSolutionState.Actions.updateTemperatures({
                selectedTemperatureUnit: activeMetricMethod,
                temperatures: datesRange.map((date, index) => ({
                    date: new Date(date).getMonth() + 1,
                    value: defaultValue,
                    index: index,
                })),
            })
        );
    } catch (ex) {
        LogService.logError(`Exception occurred in updateCropStateTemperature: ${ex}`);
        throw ex;
    }
};
export const saveProcess = (): AppThunk => async (dispatch: any, getState: any) => {
    try {
        const request: SavedItemsRequest = getSavedItemsRequest(getState);
        const response = await Api.SavedItems.saveItem(request);
        if (response && response.statusCode === 200 && response.data) {
            await dispatch(Actions.setItemId(response.data.savedItemId));
            await dispatch(Actions.setYield(response.data.yield));
            await dispatch(getAllSavedItems());
        } else if (response && response.statusCode === 200) {
            await dispatch(getAllSavedItems());
        } else if (response.statusCode === 401 || (response as any).StatusCode === 401) {
            const userWantsToBeRemembered = useSelector(GeneralState.Selectors.selectUserWantsToBeRemembered);
            await dispatch(GeneralState.Actions.logOut(userWantsToBeRemembered));
        } else {
            if (response && response.statusCode !== 200) {
                throw "Bad response from server";
            }
        }
    } catch (ex) {
        LogService.logError(`Exception occurred in saveProcess: ${ex}`);
        throw ex;
    }
};
export const saveResultState =
    (recommendedSolution: any, customizedSolution: any, onSuccess: () => void): AppThunk =>
    async (dispatch: any, getState: any) => {
        try {
            await dispatch(Actions.setSaveInProgress(true));
            await dispatch(saveProcess());
            await dispatch(
                Actions.setResultsState({
                    customizedSolution: customizedSolution,
                    recommendedSolution: recommendedSolution,
                })
            );
            await dispatch(Actions.setSaveInProgress(false));
            onSuccess();
        } catch (ex) {
            LogService.logError(`Exception occurred in saveCropStage: ${ex}`);
        } finally {
            await dispatch(Actions.setSaveInProgress(false));
        }
    };
